import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Grid,
  alpha,
  useTheme,
  Icon,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { IChartOfAccount } from "../../../../accounting.interface";
import { hasPermission } from "../../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../../auth/hooks/useAuth";

interface SplitAccountDialogProps {
  open: boolean;
  onClose: () => void;
  sourceAccount: IChartOfAccount;
  onSplit: (splitData: {
    sourceAccountId: string;
    newAccounts: Array<{
      accountName: string;
      accountCode: string;
      openingBalance: number;
      currentBalance: number;
    }>;
  }) => void;
  isLoading?: boolean;
}

interface SplitAccount {
  accountName: string;
  accountCode: string;
  openingBalance: number;
  currentBalance: number;
}

// Visual Account Node Component for Split
const SplitAccountNodeCard: React.FC<{
  account: IChartOfAccount | SplitAccount;
  title: string;
  subtitle: string;
  isSource?: boolean;
  onRemove?: () => void;
  canRemove?: boolean;
  index?: number;
}> = ({
  account,
  title,
  subtitle,
  isSource = false,
  onRemove,
  canRemove = false,
  index,
}) => {
  const theme = useTheme();

  const getAccountTypeColor = (type?: string) => {
    if (!type) return theme.palette.primary.main;
    switch (type) {
      case "Asset":
        return "#10B981";
      case "Liability":
        return "#EF4444";
      case "Equity":
        return "#3B82F6";
      case "Revenue":
        return "#8B5CF6";
      case "Expense":
        return "#F59E0B";
      default:
        return theme.palette.primary.main;
    }
  };

  const accountColor = isSource
    ? theme.palette.error.main
    : getAccountTypeColor(
        "accountType" in account ? account.accountType : undefined
      );

  const openingBalance =
    "openingBalance" in account ? account.openingBalance : 0;
  const currentBalance =
    "currentBalance" in account ? account.currentBalance : 0;
  const currency = "currency" in account ? account.currency : "USD";

  return (
    <Paper
      elevation={isSource ? 2 : 4}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `2px solid ${accountColor}`,
        bgcolor: isSource
          ? alpha(theme.palette.error.main, 0.05)
          : alpha(accountColor, 0.05),
        position: "relative",
        minHeight: 160,
        opacity: isSource ? 0.8 : 1,
      }}
    >
      {/* Remove button for split accounts */}
      {canRemove && onRemove && (
        <Tooltip title="Remove this split account">
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
            }}
          >
            <Icon sx={{ fontSize: 16, color: "error.main" }}>close</Icon>
          </IconButton>
        </Tooltip>
      )}

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: accountColor,
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: accountColor, fontWeight: 600 }}
        >
          {title}
        </Typography>
        {typeof index !== "undefined" && (
          <Chip
            label={`#${index + 1}`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              bgcolor: alpha(accountColor, 0.2),
              color: accountColor,
            }}
          />
        )}
      </Box>

      {/* Account Name */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
        {account.accountName || "New Split Account"}
      </Typography>

      {/* Account Details */}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Code: {account.accountCode || "Auto-generated"}
        {isSource && " • Will become INACTIVE"}
      </Typography>

      {/* Balances */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Opening Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {currency} {(openingBalance || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Current Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "success.main" }}
            >
              {currency} {(currentBalance || 0).toLocaleString()}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Status Badge */}
      {isSource && (
        <Chip
          label="WILL BE INACTIVE"
          size="small"
          color="error"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            fontSize: "0.7rem",
            fontWeight: 600,
          }}
        />
      )}

      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mt: 1, display: "block" }}
      >
        {subtitle}
      </Typography>
    </Paper>
  );
};

export const SplitAccountDialog: React.FC<SplitAccountDialogProps> = ({
  open,
  onClose,
  sourceAccount,
  onSplit,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [splitAccounts, setSplitAccounts] = useState<SplitAccount[]>([
    { accountName: "", accountCode: "", openingBalance: 0, currentBalance: 0 },
    { accountName: "", accountCode: "", openingBalance: 0, currentBalance: 0 },
  ]);
  const [errors, setErrors] = useState<string[]>([]);

  // Calculate totals and validation (with null safety)
  const totalOpeningBalance = splitAccounts.reduce(
    (sum, acc) => sum + (acc.openingBalance || 0),
    0
  );
  const totalCurrentBalance = splitAccounts.reduce(
    (sum, acc) => sum + (acc.currentBalance || 0),
    0
  );

  const sourceOpeningBalance = sourceAccount.openingBalance || 0;
  const sourceCurrentBalance = sourceAccount.currentBalance || 0;

  const openingBalanceMatches = totalOpeningBalance === sourceOpeningBalance;
  const currentBalanceMatches = totalCurrentBalance === sourceCurrentBalance;

  // Auto-generate account codes based on source account
  const generateAccountCode = (index: number): string => {
    const baseCode = sourceAccount.accountCode;
    const lastDigit = parseInt(baseCode.slice(-1));
    return baseCode.slice(0, -1) + (lastDigit + index + 1);
  };

  // Validate form
  useEffect(() => {
    const newErrors: string[] = [];

    // Check if all fields are filled
    splitAccounts.forEach((acc, index) => {
      if (!acc.accountName.trim()) {
        newErrors.push(`Account ${index + 1}: Name is required`);
      }
      if (!acc.accountCode.trim()) {
        newErrors.push(`Account ${index + 1}: Code is required`);
      }
      if (acc.openingBalance < 0) {
        newErrors.push(
          `Account ${index + 1}: Opening balance cannot be negative`
        );
      }
      if (acc.currentBalance < 0) {
        newErrors.push(
          `Account ${index + 1}: Current balance cannot be negative`
        );
      }
    });

    // Check for duplicate codes
    const codes = splitAccounts
      .map((acc) => acc.accountCode.trim())
      .filter((code) => code);
    const uniqueCodes = new Set(codes);
    if (codes.length !== uniqueCodes.size) {
      newErrors.push("Account codes must be unique");
    }

    // Check for duplicate names
    const names = splitAccounts
      .map((acc) => acc.accountName.trim())
      .filter((name) => name);
    const uniqueNames = new Set(names);
    if (names.length !== uniqueNames.size) {
      newErrors.push("Account names must be unique");
    }

    // Check balance matching
    if (!openingBalanceMatches) {
      newErrors.push(
        `Opening balance must total ${
          sourceAccount.currency
        } ${sourceOpeningBalance.toLocaleString()} (currently ${
          sourceAccount.currency
        } ${totalOpeningBalance.toLocaleString()})`
      );
    }

    if (!currentBalanceMatches) {
      newErrors.push(
        `Current balance must total ${
          sourceAccount.currency
        } ${sourceCurrentBalance.toLocaleString()} (currently ${
          sourceAccount.currency
        } ${totalCurrentBalance.toLocaleString()})`
      );
    }

    setErrors(newErrors);
  }, [
    splitAccounts,
    sourceOpeningBalance,
    sourceCurrentBalance,
    totalOpeningBalance,
    totalCurrentBalance,
    openingBalanceMatches,
    currentBalanceMatches,
    sourceAccount.currency,
  ]);

  const handleAddAccount = () => {
    setSplitAccounts([
      ...splitAccounts,
      {
        accountName: "",
        accountCode: generateAccountCode(splitAccounts.length),
        openingBalance: 0,
        currentBalance: 0,
      },
    ]);
  };

  const handleRemoveAccount = (index: number) => {
    if (splitAccounts.length > 2) {
      setSplitAccounts(splitAccounts.filter((_, i) => i !== index));
    }
  };

  const handleAccountChange = (
    index: number,
    field: keyof SplitAccount,
    value: string | number
  ) => {
    const updatedAccounts = [...splitAccounts];

    if (field === "openingBalance" || field === "currentBalance") {
      updatedAccounts[index][field] =
        typeof value === "string" ? parseFloat(value) || 0 : value;
    } else {
      updatedAccounts[index][field] = value as string;
    }

    setSplitAccounts(updatedAccounts);
  };

  const handleAutoGenerateCodes = () => {
    const updatedAccounts = splitAccounts.map((acc, index) => ({
      ...acc,
      accountCode: generateAccountCode(index),
    }));
    setSplitAccounts(updatedAccounts);
  };

  const handleSplit = () => {
    if (errors.length === 0) {
      onSplit({
        sourceAccountId: sourceAccount.id,
        newAccounts: splitAccounts,
      });
    }
  };

  const isValid =
    errors.length === 0 &&
    splitAccounts.every(
      (acc) => acc.accountName.trim() && acc.accountCode.trim()
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: alpha(theme.palette.warning.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Icon sx={{ color: "warning.main", fontSize: 28 }}>call_split</Icon>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "warning.main" }}
            >
              Split Account
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Split "{sourceAccount.accountName}" into multiple new accounts
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Visual Split Flow */}
        <Box sx={{ my: 4 }}>
          <Grid container spacing={3}>
            {/* Source Account */}
            <Grid item xs={12} lg={4}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: "error.main",
                    textAlign: "center",
                  }}
                >
                  Source Account (Will Become Inactive)
                </Typography>
                <SplitAccountNodeCard
                  account={sourceAccount}
                  title="ORIGINAL ACCOUNT"
                  subtitle="Will be marked inactive after split"
                  isSource={true}
                />
              </Box>
            </Grid>

            {/* Split Arrow */}
            <Grid item xs={12} lg={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 200,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 40,
                      color: "warning.main",
                      transform: { lg: "rotate(0deg)", xs: "rotate(90deg)" },
                    }}
                  >
                    trending_flat
                  </Icon>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "warning.main",
                      fontWeight: 600,
                      textAlign: "center",
                      writingMode: { lg: "horizontal-tb", xs: "initial" },
                    }}
                  >
                    SPLIT INTO
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Split Accounts Grid */}
            <Grid item xs={12} lg={7}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "success.main" }}
                  >
                    New Split Accounts ({splitAccounts.length})
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAutoGenerateCodes}
                      startIcon={<Icon>auto_fix_high</Icon>}
                      sx={{ textTransform: "none" }}
                    >
                      Auto-Generate Codes
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddAccount}
                      startIcon={<Icon>add</Icon>}
                      sx={{ textTransform: "none" }}
                    >
                      Add Account
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  {splitAccounts.map((account, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <SplitAccountNodeCard
                        account={account}
                        title={`NEW ACCOUNT ${index + 1}`}
                        subtitle="Will be created from split"
                        onRemove={() => handleRemoveAccount(index)}
                        canRemove={splitAccounts.length > 2}
                        index={index}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Account Configuration Forms */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Configure Split Accounts
          </Typography>
          {splitAccounts.map((account, index) => (
            <Paper
              key={index}
              sx={{
                p: 3,
                mb: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Account {index + 1} Configuration
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={account.accountName}
                    onChange={(e) =>
                      handleAccountChange(index, "accountName", e.target.value)
                    }
                    placeholder={`Split from ${sourceAccount.accountName}`}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Code"
                    value={account.accountCode}
                    onChange={(e) =>
                      handleAccountChange(index, "accountCode", e.target.value)
                    }
                    placeholder={generateAccountCode(index)}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Opening Balance"
                    type="number"
                    value={account.openingBalance}
                    onChange={(e) =>
                      handleAccountChange(
                        index,
                        "openingBalance",
                        e.target.value
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1, color: "text.secondary" }}>
                          {sourceAccount.currency}
                        </Typography>
                      ),
                    }}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Balance"
                    type="number"
                    value={account.currentBalance}
                    onChange={(e) =>
                      handleAccountChange(
                        index,
                        "currentBalance",
                        e.target.value
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1, color: "text.secondary" }}>
                          {sourceAccount.currency}
                        </Typography>
                      ),
                    }}
                    disabled={isLoading}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Balance Summary */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: alpha(
              openingBalanceMatches && currentBalanceMatches
                ? theme.palette.success.main
                : theme.palette.warning.main,
              0.04
            ),
            border: `1px solid ${alpha(
              openingBalanceMatches && currentBalanceMatches
                ? theme.palette.success.main
                : theme.palette.warning.main,
              0.2
            )}`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon>calculate</Icon>
            Balance Validation Summary
          </Typography>

          <Grid container spacing={3}>
            {/* Opening Balance Summary */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                >
                  Opening Balance Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {sourceAccount.currency}{" "}
                  {totalOpeningBalance.toLocaleString()}
                </Typography>
                <Chip
                  label={openingBalanceMatches ? "✓ Matches" : "✗ Mismatch"}
                  color={openingBalanceMatches ? "success" : "warning"}
                  size="small"
                />
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1, color: "text.secondary" }}
                >
                  Required: {sourceAccount.currency}{" "}
                  {sourceOpeningBalance.toLocaleString()}
                </Typography>
              </Box>
            </Grid>

            {/* Current Balance Summary */}
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mb: 1, color: "success.main" }}
                >
                  Current Balance Total
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {sourceAccount.currency}{" "}
                  {totalCurrentBalance.toLocaleString()}
                </Typography>
                <Chip
                  label={currentBalanceMatches ? "✓ Matches" : "✗ Mismatch"}
                  color={currentBalanceMatches ? "success" : "warning"}
                  size="small"
                />
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1, color: "text.secondary" }}
                >
                  Required: {sourceAccount.currency}{" "}
                  {sourceCurrentBalance.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Validation Errors */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Please fix the following issues:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {errors.map((error, index) => (
                <Box component="li" key={index} sx={{ mb: 0.5 }}>
                  <Typography variant="body2">{error}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSplit}
          disabled={
            !isValid ||
            isLoading ||
            !hasPermission(
              "accounting.chart_of_accounts.split_accounts",
              user?.newPermissions,
              user?.assignment_details?.id,
              user?.is_store_owner
            )
          }
          startIcon={
            isLoading ? <Icon>hourglass_empty</Icon> : <Icon>call_split</Icon>
          }
        >
          {isLoading ? "Splitting..." : "Split Account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
